'use client';

import { c as _c } from "react/compiler-runtime";
import { AppSearchBar } from '@/components/ui/app-search-bar';
import { HighlightedText } from '@/features/payload-cms/components/search/highlighted-text';
import { rememberSearch } from '@/features/payload-cms/components/search/recent-searches';
import { filterChipClassName } from '@/features/payload-cms/components/search/search-filter-panel';
import { SearchPreview } from '@/features/payload-cms/components/search/search-preview';
import { resultMetaParts, resultTypeIcons } from '@/features/payload-cms/components/search/search-result-meta';
import { InstantRowsSkeleton, PreviewSkeleton } from '@/features/payload-cms/components/search/search-skeleton';
import { RecentSearches } from '@/features/payload-cms/components/search/search-suggestions';
import { allTypesLabel, fullViewLabel, instantCloseKeyLabel, instantErrorLabel, instantHintLabel, instantNavigateKeyLabel, instantOfflineLabel, instantOpenKeyLabel, noResultsDescription, noResultsTitle, searchPlaceholder, typeLabels } from '@/features/payload-cms/components/search/search-translations';
import { MIN_QUERY_LENGTH, QUERY_DELAY_MS } from '@/features/payload-cms/components/search/use-live-search';
import { parseSearchWords } from '@/features/payload-cms/search/search-text';
import type { SearchResult, SearchResultType } from '@/features/payload-cms/search/search-types';
import { buildSearchQueryString, SEARCH_RESULT_TYPES } from '@/features/payload-cms/search/search-types';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { useOnlineStatus } from '@/hooks/use-online-status';
import { trpc } from '@/trpc/client';
import type { Locale } from '@/types/types';
import { cn } from '@/utils/tailwindcss-override';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { ChevronRight, CornerDownLeft, SearchX, WifiOff } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import type React from 'react';
import { useRef, useState } from 'react';
const Key: React.FC<{
  children: React.ReactNode;
}> = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <kbd className="border-line text-heading rounded-[5px] border bg-white px-1.5 py-0.5 font-sans text-[11px] font-semibold">{children}</kbd>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};

/**
 * The results in the order the list shows them: grouped by type, the group of the best result
 * first, best first in each group.
 */
const groupByType = (results: SearchResult[]): [SearchResultType, SearchResult[]][] => {
  const types = [...new Set(results.map(result => result.type))];
  return types.map(type => [type, results.filter(result => result.type === type)]);
};
const InstantResultRow: React.FC<{
  result: SearchResult;
  words: string[];
  locale: Locale;
  isSelected: boolean;
  onHover: () => void;
  onOpen: () => void;
  rowReference: (element: HTMLAnchorElement | null) => void;
}> = t0 => {
  const $ = _c(40);
  const {
    result,
    words,
    locale,
    isSelected,
    onHover,
    onOpen,
    rowReference
  } = t0;
  const Icon = resultTypeIcons[result.type];
  const isDocument = result.type === "document";
  const t1 = result.url;
  const t2 = !isDocument && isSelected;
  let t3;
  if ($[0] !== isDocument) {
    t3 = isDocument ? {
      target: "_blank",
      rel: "noopener"
    } : {};
    $[0] = isDocument;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = isSelected ? "bg-conveniat-green text-white" : "bg-quiet text-heading";
  let t5;
  if ($[2] !== t4) {
    t5 = cn("flex min-h-14 w-full cursor-pointer items-center gap-3 rounded-[10px] px-3 py-2.5 text-left", t4);
    $[2] = t4;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const t6 = isSelected ? "bg-white/15 text-white" : "text-conveniat-green bg-white";
  let t7;
  if ($[4] !== t6) {
    t7 = cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-[9px]", t6);
    $[4] = t6;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] !== Icon) {
    t8 = <Icon className="h-[18px] w-[18px]" aria-hidden="true" />;
    $[6] = Icon;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== t7 || $[9] !== t8) {
    t9 = <span className={t7}>{t8}</span>;
    $[8] = t7;
    $[9] = t8;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== isSelected || $[12] !== result.title || $[13] !== words) {
    t10 = <span className="truncate text-[15px] font-bold"><HighlightedText text={result.title} words={words} onDark={isSelected} /></span>;
    $[11] = isSelected;
    $[12] = result.title;
    $[13] = words;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const t11 = isSelected ? "text-white/80" : "text-green-500";
  let t12;
  if ($[15] !== t11) {
    t12 = cn("truncate text-[13px]", t11);
    $[15] = t11;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== locale || $[18] !== result) {
    t13 = resultMetaParts(result, locale).join(" \xB7 ");
    $[17] = locale;
    $[18] = result;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== t12 || $[21] !== t13) {
    t14 = <span className={t12}>{t13}</span>;
    $[20] = t12;
    $[21] = t13;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== t10 || $[24] !== t14) {
    t15 = <span className="flex min-w-0 flex-1 flex-col">{t10}{t14}</span>;
    $[23] = t10;
    $[24] = t14;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== isSelected) {
    t16 = isSelected && <CornerDownLeft className="h-4 w-4 shrink-0" aria-hidden="true" />;
    $[26] = isSelected;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== isSelected || $[29] !== onHover || $[30] !== onOpen || $[31] !== result.url || $[32] !== rowReference || $[33] !== t15 || $[34] !== t16 || $[35] !== t2 || $[36] !== t3 || $[37] !== t5 || $[38] !== t9) {
    t17 = <Link ref={rowReference} href={t1} role="option" aria-selected={isSelected} prefetch={t2} {...t3} onMouseMove={onHover} onClick={onOpen} className={t5}>{t9}{t15}{t16}</Link>;
    $[28] = isSelected;
    $[29] = onHover;
    $[30] = onOpen;
    $[31] = result.url;
    $[32] = rowReference;
    $[33] = t15;
    $[34] = t16;
    $[35] = t2;
    $[36] = t3;
    $[37] = t5;
    $[38] = t9;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  return t17;
};
const Notice: React.FC<{
  icon: React.ReactNode;
  title: string;
  description?: string;
}> = t0 => {
  const $ = _c(8);
  const {
    icon,
    title,
    description
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <p className="text-heading mt-3 text-[15px] font-semibold">{title}</p>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== description) {
    t2 = description !== undefined && <p className="mt-1 text-[14px] text-green-700">{description}</p>;
    $[2] = description;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== icon || $[5] !== t1 || $[6] !== t2) {
    t3 = <div className="flex flex-col items-center px-6 py-12 text-center">{icon}{t1}{t2}</div>;
    $[4] = icon;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};

/**
 * The search a desktop opens from the header or with Ctrl K: results while typing, grouped by
 * type, the chosen one previewed beside them, and the way to the full search page. Phones go to
 * the search page directly; this would only cover their screen.
 */
export const InstantSearchDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  locale: Locale;
  /** the search page, with the locale prefix */
  searchPath: string;
}> = t0 => {
  const $ = _c(121);
  const {
    open,
    onClose,
    locale,
    searchPath
  } = t0;
  const router = useRouter();
  const isOnline = useOnlineStatus();
  const [query, setQuery] = useState("");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [types, setTypes] = useState(t1);
  const [selectedKey, setSelectedKey] = useState();
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const rowReferences = useRef(t2);
  let t3;
  if ($[2] !== query) {
    t3 = query.trim();
    $[2] = query;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const trimmed = t3;
  const debouncedQuery = useDebouncedValue(trimmed, QUERY_DELAY_MS);
  const isTyping = trimmed !== debouncedQuery;
  const isSearchable = debouncedQuery.length >= MIN_QUERY_LENGTH;
  let t4;
  if ($[4] !== debouncedQuery || $[5] !== locale || $[6] !== types) {
    t4 = {
      locale,
      query: debouncedQuery,
      types
    };
    $[4] = debouncedQuery;
    $[5] = locale;
    $[6] = types;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = open && isSearchable && isOnline;
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      persist: false
    };
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t5) {
    t7 = {
      enabled: t5,
      staleTime: 60000,
      meta: t6
    };
    $[9] = t5;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const searchQuery = trpc.search.getSearchResultList.useQuery(t4, t7);
  const data = isTyping || !isSearchable ? undefined : searchQuery.data;
  let t8;
  if ($[11] !== debouncedQuery) {
    t8 = parseSearchWords(debouncedQuery);
    $[11] = debouncedQuery;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const words = t8;
  let T0;
  let T1;
  let openFullView;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t9;
  if ($[13] !== data || $[14] !== isOnline || $[15] !== isSearchable || $[16] !== isTyping || $[17] !== locale || $[18] !== onClose || $[19] !== open || $[20] !== query || $[21] !== router || $[22] !== searchPath || $[23] !== searchQuery || $[24] !== selectedKey || $[25] !== trimmed || $[26] !== types || $[27] !== words) {
    const groups = isSearchable && data !== undefined ? groupByType(data.results) : [];
    const ordered = groups.flatMap(_temp);
    const selected = ordered.find(result => result.key === selectedKey) ?? ordered[0];
    const selectedIndex = selected === undefined ? -1 : ordered.indexOf(selected);
    let t19;
    if ($[41] !== data) {
      t19 = data === undefined ? 0 : SEARCH_RESULT_TYPES.reduce((sum, type) => sum + data.counts[type], 0);
      $[41] = data;
      $[42] = t19;
    } else {
      t19 = $[42];
    }
    const totalFound = t19;
    const fullViewHref = `${searchPath}?${buildSearchQueryString({
      query: query.trim(),
      types
    })}`;
    let t20;
    if ($[43] !== onClose) {
      t20 = () => {
        onClose();
        setQuery("");
        setTypes([]);
      };
      $[43] = onClose;
      $[44] = t20;
    } else {
      t20 = $[44];
    }
    const close = t20;
    let t21;
    if ($[45] !== close || $[46] !== router || $[47] !== trimmed) {
      t21 = chosen => {
        rememberSearch(trimmed);
        if (chosen.type === "document") {
          globalThis.open(chosen.url, "_blank", "noopener");
        } else {
          router.push(chosen.url);
        }
        close();
      };
      $[45] = close;
      $[46] = router;
      $[47] = trimmed;
      $[48] = t21;
    } else {
      t21 = $[48];
    }
    const openResult = t21;
    let t22;
    if ($[49] !== close || $[50] !== fullViewHref || $[51] !== router || $[52] !== trimmed) {
      t22 = () => {
        if (trimmed === "") {
          return;
        }
        rememberSearch(trimmed);
        router.push(fullViewHref);
        close();
      };
      $[49] = close;
      $[50] = fullViewHref;
      $[51] = router;
      $[52] = trimmed;
      $[53] = t22;
    } else {
      t22 = $[53];
    }
    openFullView = t22;
    const moveSelection = step => {
      if (ordered.length === 0) {
        return;
      }
      const next = (selectedIndex + step + ordered.length) % ordered.length;
      setSelectedKey(ordered[next]?.key);
      rowReferences.current[next]?.scrollIntoView({
        block: "nearest"
      });
    };
    const handleKeyDown = event => {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        moveSelection(event.key === "ArrowDown" ? 1 : -1);
      } else {
        if (event.key === "Enter") {
          event.preventDefault();
          if (event.ctrlKey || event.metaKey) {
            openFullView();
          } else {
            if (selected === undefined) {
              openFullView();
            } else {
              openResult(selected);
            }
          }
        }
      }
    };
    let t23;
    if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
      t23 = type_0 => setTypes(chosen_0 => chosen_0.includes(type_0) ? chosen_0.filter(entry => entry !== type_0) : [...chosen_0, type_0]);
      $[54] = t23;
    } else {
      t23 = $[54];
    }
    const toggleType = t23;
    const renderBody = () => {
      if (!isOnline) {
        return <Notice icon={<WifiOff className="h-7 w-7 text-green-500" aria-hidden="true" />} title={instantOfflineLabel[locale]} />;
      }
      if (trimmed.length < MIN_QUERY_LENGTH) {
        return <div className="px-5 py-6"><p className="text-[15px] text-green-700">{instantHintLabel[locale]}</p><RecentSearches locale={locale} onPick={setQuery} className="mt-5" /></div>;
      }
      if (!isTyping && searchQuery.isError && data === undefined) {
        return <Notice icon={<SearchX className="h-7 w-7 text-green-500" aria-hidden="true" />} title={instantErrorLabel[locale]} />;
      }
      if (data === undefined) {
        return <div aria-busy="true" className="grid min-h-0 flex-1 grid-cols-[minmax(0,420px)_minmax(0,1fr)]"><InstantRowsSkeleton /><div className="border-line bg-page border-l px-6 py-5"><PreviewSkeleton /></div></div>;
      }
      if (ordered.length === 0) {
        return <><Notice icon={<SearchX className="h-7 w-7 text-green-500" aria-hidden="true" />} title={noResultsTitle[locale]} description={noResultsDescription[locale]} /><RecentSearches locale={locale} current={trimmed} onPick={setQuery} className="px-5 pb-6" /></>;
      }
      let index = -1;
      return <div className="grid min-h-0 flex-1 grid-cols-[minmax(0,420px)_minmax(0,1fr)]"><div role="listbox" className="overflow-y-auto px-3 py-3">{groups.map(t24 => {
            const [type_1, group_0] = t24;
            return <section key={type_1} className="mb-3"><h3 className="px-1 pb-1.5 text-xs font-bold tracking-[0.08em] text-green-700 uppercase">{typeLabels[type_1][locale]}</h3><div className="flex flex-col gap-1.5">{group_0.map(result_0 => {
                  index = index + 1;
                  index;
                  const rowIndex = index;
                  return <InstantResultRow key={result_0.key} result={result_0} words={words} locale={locale} isSelected={result_0.key === selected?.key} onHover={() => setSelectedKey(result_0.key)} onOpen={() => {
                    rememberSearch(trimmed);
                    close();
                  }} rowReference={element => {
                    rowReferences.current[rowIndex] = element;
                  }} />;
                })}</div></section>;
          })}</div><div onClick={event_0 => {
          if (event_0.target instanceof Element && event_0.target.closest("a") !== null) {
            rememberSearch(trimmed);
            close();
          }
        }} className="border-line bg-page overflow-y-auto border-l px-6 py-5">{selected !== undefined && <SearchPreview key={selected.key} result={selected} words={words} locale={locale} />}</div></div>;
    };
    T1 = Dialog;
    t15 = open;
    t16 = close;
    t17 = "relative z-[100]";
    if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = <DialogBackdrop className="fixed inset-0 bg-gray-950/30" />;
      $[55] = t18;
    } else {
      t18 = $[55];
    }
    t14 = "fixed inset-0 flex justify-center px-8 pt-[8vh]";
    T0 = DialogPanel;
    t9 = handleKeyDown;
    t10 = "flex h-fit max-h-[84vh] w-full max-w-[1080px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl";
    const t25 = searchPlaceholder[locale];
    if ($[56] !== t25) {
      t11 = <DialogTitle className="sr-only">{t25}</DialogTitle>;
      $[56] = t25;
      $[57] = t11;
    } else {
      t11 = $[57];
    }
    let t26;
    let t27;
    if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
      t26 = event_1 => setQuery(event_1.target.value);
      t27 = () => setQuery("");
      $[58] = t26;
      $[59] = t27;
    } else {
      t26 = $[58];
      t27 = $[59];
    }
    const t28 = searchPlaceholder[locale];
    let t29;
    if ($[60] !== query || $[61] !== t28) {
      t29 = <AppSearchBar value={query} onChange={t26} onClear={t27} placeholder={t28} autoFocus={true} className="flex-1" />;
      $[60] = query;
      $[61] = t28;
      $[62] = t29;
    } else {
      t29 = $[62];
    }
    let t30;
    if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
      t30 = <Key>Esc</Key>;
      $[63] = t30;
    } else {
      t30 = $[63];
    }
    let t31;
    if ($[64] !== t29) {
      t31 = <div className="flex items-center gap-3">{t29}{t30}</div>;
      $[64] = t29;
      $[65] = t31;
    } else {
      t31 = $[65];
    }
    const t32 = types.length === 0;
    let t33;
    if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
      t33 = () => setTypes([]);
      $[66] = t33;
    } else {
      t33 = $[66];
    }
    const t34 = types.length === 0;
    let t35;
    if ($[67] !== t34) {
      t35 = filterChipClassName(t34);
      $[67] = t34;
      $[68] = t35;
    } else {
      t35 = $[68];
    }
    const t36 = allTypesLabel[locale];
    let t37;
    if ($[69] !== data || $[70] !== isSearchable || $[71] !== totalFound) {
      t37 = data !== undefined && isSearchable && <span className="text-[12px] opacity-75">{totalFound}</span>;
      $[69] = data;
      $[70] = isSearchable;
      $[71] = totalFound;
      $[72] = t37;
    } else {
      t37 = $[72];
    }
    let t38;
    if ($[73] !== t32 || $[74] !== t35 || $[75] !== t36 || $[76] !== t37) {
      t38 = <button type="button" aria-pressed={t32} onClick={t33} className={t35}>{t36}{t37}</button>;
      $[73] = t32;
      $[74] = t35;
      $[75] = t36;
      $[76] = t37;
      $[77] = t38;
    } else {
      t38 = $[77];
    }
    let t39;
    if ($[78] !== data || $[79] !== isSearchable || $[80] !== locale || $[81] !== types) {
      t39 = SEARCH_RESULT_TYPES.map(type_2 => <button key={type_2} type="button" aria-pressed={types.includes(type_2)} onClick={() => toggleType(type_2)} className={filterChipClassName(types.includes(type_2))}>{typeLabels[type_2][locale]}{data !== undefined && isSearchable && <span className="text-[12px] opacity-75">{data.counts[type_2]}</span>}</button>);
      $[78] = data;
      $[79] = isSearchable;
      $[80] = locale;
      $[81] = types;
      $[82] = t39;
    } else {
      t39 = $[82];
    }
    let t40;
    if ($[83] !== t38 || $[84] !== t39) {
      t40 = <div className="mt-3 flex flex-wrap gap-2">{t38}{t39}</div>;
      $[83] = t38;
      $[84] = t39;
      $[85] = t40;
    } else {
      t40 = $[85];
    }
    if ($[86] !== t31 || $[87] !== t40) {
      t12 = <div className="border-line border-b px-4 pt-4 pb-3">{t31}{t40}</div>;
      $[86] = t31;
      $[87] = t40;
      $[88] = t12;
    } else {
      t12 = $[88];
    }
    t13 = renderBody();
    $[13] = data;
    $[14] = isOnline;
    $[15] = isSearchable;
    $[16] = isTyping;
    $[17] = locale;
    $[18] = onClose;
    $[19] = open;
    $[20] = query;
    $[21] = router;
    $[22] = searchPath;
    $[23] = searchQuery;
    $[24] = selectedKey;
    $[25] = trimmed;
    $[26] = types;
    $[27] = words;
    $[28] = T0;
    $[29] = T1;
    $[30] = openFullView;
    $[31] = t10;
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
    $[35] = t14;
    $[36] = t15;
    $[37] = t16;
    $[38] = t17;
    $[39] = t18;
    $[40] = t9;
  } else {
    T0 = $[28];
    T1 = $[29];
    openFullView = $[30];
    t10 = $[31];
    t11 = $[32];
    t12 = $[33];
    t13 = $[34];
    t14 = $[35];
    t15 = $[36];
    t16 = $[37];
    t17 = $[38];
    t18 = $[39];
    t9 = $[40];
  }
  let t19;
  if ($[89] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <Key>↵</Key>;
    $[89] = t19;
  } else {
    t19 = $[89];
  }
  const t20 = instantOpenKeyLabel[locale];
  let t21;
  let t22;
  if ($[90] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <Key>↑</Key>;
    t22 = <Key>↓</Key>;
    $[90] = t21;
    $[91] = t22;
  } else {
    t21 = $[90];
    t22 = $[91];
  }
  const t23 = instantNavigateKeyLabel[locale];
  let t24;
  if ($[92] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <Key>Esc</Key>;
    $[92] = t24;
  } else {
    t24 = $[92];
  }
  const t25 = instantCloseKeyLabel[locale];
  let t26;
  if ($[93] !== data || $[94] !== isSearchable || $[95] !== locale || $[96] !== openFullView) {
    t26 = data !== undefined && isSearchable && data.total > 0 && <button type="button" onClick={openFullView} className="text-conveniat-green ml-auto flex min-h-11 cursor-pointer items-center gap-1.5 font-semibold">{fullViewLabel(data.total)[locale]}<Key>Ctrl ↵</Key><ChevronRight className="h-4 w-4" aria-hidden="true" /></button>;
    $[93] = data;
    $[94] = isSearchable;
    $[95] = locale;
    $[96] = openFullView;
    $[97] = t26;
  } else {
    t26 = $[97];
  }
  let t27;
  if ($[98] !== t20 || $[99] !== t23 || $[100] !== t25 || $[101] !== t26) {
    t27 = <div className="border-line bg-page flex items-center gap-2 border-t px-4 py-2.5 text-[13px] text-green-700">{t19} {t20}{t21}{t22} {t23}{t24} {t25}{t26}</div>;
    $[98] = t20;
    $[99] = t23;
    $[100] = t25;
    $[101] = t26;
    $[102] = t27;
  } else {
    t27 = $[102];
  }
  let t28;
  if ($[103] !== T0 || $[104] !== t10 || $[105] !== t11 || $[106] !== t12 || $[107] !== t13 || $[108] !== t27 || $[109] !== t9) {
    t28 = <T0 onKeyDown={t9} className={t10}>{t11}{t12}{t13}{t27}</T0>;
    $[103] = T0;
    $[104] = t10;
    $[105] = t11;
    $[106] = t12;
    $[107] = t13;
    $[108] = t27;
    $[109] = t9;
    $[110] = t28;
  } else {
    t28 = $[110];
  }
  let t29;
  if ($[111] !== t14 || $[112] !== t28) {
    t29 = <div className={t14}>{t28}</div>;
    $[111] = t14;
    $[112] = t28;
    $[113] = t29;
  } else {
    t29 = $[113];
  }
  let t30;
  if ($[114] !== T1 || $[115] !== t15 || $[116] !== t16 || $[117] !== t17 || $[118] !== t18 || $[119] !== t29) {
    t30 = <T1 open={t15} onClose={t16} className={t17}>{t18}{t29}</T1>;
    $[114] = T1;
    $[115] = t15;
    $[116] = t16;
    $[117] = t17;
    $[118] = t18;
    $[119] = t29;
    $[120] = t30;
  } else {
    t30 = $[120];
  }
  return t30;
};
function _temp(t0) {
  const [, group] = t0;
  return group;
}